import { useId, useState, type ReactNode } from 'react'
import { ApiClientError } from 'grabbitbot-legacy-ui'
import type { ApiQueryState } from 'grabbitbot-legacy-ui'
import { describeError } from '../api/errorMessages'
import type { MoneyEntry } from '../api/financeTypes'
import { DASH, formatMoney } from '../lib/money'
import { HELP } from './helpTexts'

export function HelpButton({ label, open, onToggle, controls }: { label: string; open: boolean; onToggle: () => void; controls?: string }) {
  return (
    <button type="button" className="af-help-btn" aria-label={`Magyarázat: ${label}`} aria-expanded={open} aria-controls={controls} onClick={onToggle}>
      <span aria-hidden="true">?</span>
    </button>
  )
}

export function HelpBody({ helpKey, id }: { helpKey: string; id?: string }) {
  const h = HELP[helpKey]
  if (!h) return null
  return (
    <div className="af-help" id={id} role="note">
      <p>
        <strong>Mit jelent:</strong> {h.what}
      </p>
      <p>
        <strong>Melyik irány a jó:</strong> {h.direction}
      </p>
      <p>
        <strong>Jó tartomány:</strong> {h.range}
      </p>
    </div>
  )
}

/** A label with its help icon; the explanation opens UNDER the label (not a
 * hover tooltip, so it works on a phone too). */
export function HelpLabel({ helpKey, children, as: Tag = 'span', className }: { helpKey: string; children: ReactNode; as?: 'span' | 'h2' | 'h3'; className?: string }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const text = typeof children === 'string' ? children : helpKey
  return (
    <>
      <div className="af-label-row">
        <Tag className={className}>{children}</Tag>
        <HelpButton label={text} open={open} onToggle={() => setOpen(!open)} controls={id} />
      </div>
      {open && <HelpBody helpKey={helpKey} id={id} />}
    </>
  )
}

/** Column-header help: the header carries only the icon; the opened
 * explanation sits under the table title (see `ColumnHelpPanel`). */
export function ColumnHelpHeader({ label, helpKey, openKey, setOpenKey }: { label: string; helpKey: string; openKey: string | null; setOpenKey: (k: string | null) => void }) {
  const open = openKey === helpKey
  return (
    <span className="af-th">
      {label}
      <span
        role="presentation"
        onClick={(e) => e.stopPropagation()}
      >
        <HelpButton label={label} open={open} onToggle={() => setOpenKey(open ? null : helpKey)} controls="af-col-help" />
      </span>
    </span>
  )
}

export function ColumnHelpPanel({ openKey }: { openKey: string | null }) {
  if (!openKey) return null
  return <HelpBody helpKey={openKey} id="af-col-help" />
}

export type Tone = 'ok' | 'warn' | 'crit' | 'info' | 'mute'

export function Chip({ tone, children, title }: { tone: Tone; children: ReactNode; title?: string }) {
  return (
    <span className={`af-chip af-chip--${tone}`} title={title}>
      {children}
    </span>
  )
}

/** A figure that could not be computed: `—`, with the API's reason on hover
 * and in text for screen readers. Never `0`. */
export function NullValue({ reason }: { reason?: string }) {
  return (
    <span className="af-null" title={reason ? `Nem számolható: ${reason}` : 'Nincs adat'}>
      {DASH}
      {reason && <span className="af-sr">{` (${reason})`}</span>}
    </span>
  )
}

/** One line per currency — never a combined total. */
export function MoneyLines({ list, reason, empty = '0' }: { list: MoneyEntry[] | null | undefined; reason?: string; empty?: string }) {
  if (list === null || list === undefined) return <NullValue reason={reason} />
  if (list.length === 0) return <span className="af-num">{empty}</span>
  return (
    <span className="af-money-lines">
      {list.map((m) => (
        <span key={m.currency} className="af-num">
          {formatMoney(m.amount, m.currency)}
        </span>
      ))}
    </span>
  )
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={`af-card${className ? ` ${className}` : ''}`}>{children}</section>
}

/** Loading / error wrapper for the finance calls. A 403 is an explicit "no
 * access" state — absence of rights must never look like absence of data. */
export function FinanceQuery<T>({ q, children }: { q: ApiQueryState<T>; children: (data: T) => ReactNode }) {
  if (q.error) return <FinanceErrorNotice error={q.error} />
  if (q.loading && !q.data) return <p className="portal-text-muted">Betöltés…</p>
  if (!q.data) return null
  return <>{children(q.data)}</>
}

export function FinanceErrorNotice({ error }: { error: unknown }) {
  const status = error instanceof ApiClientError ? error.status : 0
  const code = error instanceof ApiClientError ? error.code : ''
  let text: string
  if (status === 403) text = 'Nincs jogosultság: ehhez a projekthez vagy oldalhoz nem rendelkezel admin joggal.'
  else if (status === 422 && code === 'project_required') text = 'Válassz projektet a hatókör-választóban.'
  else if (status === 404 && code === 'user_not_found') text = 'Nincs ilyen felhasználó a kiválasztott hatókörben.'
  else if (status === 404 && code === 'project_not_found') text = 'A kiválasztott projekt nem található.'
  else text = describeError(error, 'A betöltés nem sikerült')
  return (
    <p role="alert" className="portal-notice portal-notice--error" data-testid="finance-error">
      {text}
    </p>
  )
}

export function PageHead({ title, sub, children }: { title: string; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="af-page-head">
      <div>
        <h1>{title}</h1>
        {sub && <p className="af-sub">{sub}</p>}
      </div>
      {children && <div className="af-page-actions">{children}</div>}
    </div>
  )
}

export function Segmented<T extends string>({ options, value, onChange, label }: { options: { key: T; label: string }[]; value: T; onChange: (k: T) => void; label: string }) {
  return (
    <div className="af-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.key} type="button" aria-pressed={value === o.key} className={value === o.key ? 'on' : ''} onClick={() => onChange(o.key)}>
          {o.label}
        </button>
      ))}
    </div>
  )
}
